import { defineStore } from 'pinia'
import { GiphyFetch } from '@giphy/js-fetch-api'
import { IGif } from '@giphy/js-types'
import { GalleryItem, GalleryCollection } from '@/store/gallery/types'
import * as seratoMediaStock from '@/serato-media-stock'
import { seratoMediaItemToGalleryItem, seratoCollectionToGalleryCollection, collectionNameSorter } from '@/utils/gallery-helper'
import useStoreErrorHandler from '@/composables/UseStoreErrorHandler'
 
const { handleSearchError } = useStoreErrorHandler()
const giphyClient = new GiphyFetch('egjfyXmV05B0CyPjRv2iUkyvUCV8QCDp')
export interface StickerGalleryState {
  items: GalleryItem[],
  collections: GalleryCollection[],
  page: number
  totalResultItems: number
  itemsPerPage: number
  searchPhrase: string | null,
  selectedCollection: GalleryCollection | null
}

const collectionsOrder = [
  'Emoji',
  'Playful',
  'Serato',
  'Retro',
  'Trippy'
]

const giphyStickerToGalleryItem = (sticker: IGif): GalleryItem => {
  return {
    type: 'sticker',
    widthToHeightRatio: sticker.images.downsized.width / sticker.images.downsized.height,
    preview: {
      imageSource: {
        src: sticker.images.preview_gif.url,
        width: sticker.images.preview_gif.width,
        height: sticker.images.preview_gif.height
      },
      videoSource: null
    },
    // image size up to 2MB
    source: [{
      width: sticker.images.downsized.width,
      height: sticker.images.downsized.height,
      src: sticker.images.downsized.url
    }]
  }
}

export const useStickerGalleryStore = defineStore('stickerGalleryStore', {
  state: (): StickerGalleryState => ({
    items: [],
    collections: [],
    page: 1,
    totalResultItems: 0,
    itemsPerPage: 15,
    searchPhrase: null,
    selectedCollection: null
  }),
  actions: {
    async clearItems (): Promise<void> {
      // this needs to be inside an async/await function because it triggers nextTick for watch to pick up the changes.
      this.items = []
    },
    async fetchByCollection (collection: GalleryCollection, page = 1): Promise<void> {
      this.selectedCollection = collection
      if (page === 1) {
        await this.clearItems()
      }
      switch (collection.source) {
      case 'serato': {
        const response = seratoMediaStock.fetchStickersByCollectionId(collection.id, page, this.itemsPerPage)
        const items = response.pageItems.map((mediaItem) => {
          return seratoMediaItemToGalleryItem(mediaItem)
        })
        this.items.push(...items)
        this.totalResultItems = response.totalResults
        break
      }
      }
      this.page = page
      this.searchPhrase = null
    },
    async fetchCollections (): Promise<void> {
      this.collections = []
      // Fetching Serato media stock collections
      seratoMediaStock.fetchStickerCollections().forEach(collection => {
        this.collections.push(seratoCollectionToGalleryCollection(collection))
      })
      this.collections.sort(collectionNameSorter(collectionsOrder))
    },
    async fetchByKeyword (keyword: string, page = 1): Promise<void> {
      try {
        if (page === 1) {
          await this.clearItems()
        }
        // Fetching sticker only
        const response = await giphyClient.search(keyword, {
          type: 'stickers',
          limit: this.itemsPerPage,
          offset: this.itemsPerPage * (page - 1),
          rating: 'pg-13'
        })

        const items = response.data.map((sticker) => giphyStickerToGalleryItem(sticker))
        this.items.push(...items)
        this.page = page
        this.searchPhrase = keyword
        this.totalResultItems = response.pagination.total_count
      } catch {
        handleSearchError('Giphy')
      } finally {
        this.selectedCollection = null
      }
    },
    async fetchTrending (page = 1): Promise<void> {
      try {
        if (page === 1) {
          this.items = []
        }

        const response = await giphyClient.trending({
          type: 'stickers',
          limit: this.itemsPerPage,
          offset: this.itemsPerPage * (page - 1),
          rating: 'g'
        })

        const items = response.data.map((sticker) => giphyStickerToGalleryItem(sticker))
        this.items.push(...items)
        this.page = page
        this.searchPhrase = null
        this.totalResultItems = response.pagination.total_count
      } catch {
        handleSearchError('Giphy')
      } finally {
        this.selectedCollection = null
      }
    },
    async nextPage (): Promise<void> {
      const pages = Math.ceil(this.totalResultItems / this.itemsPerPage)

      if (this.page < pages) {
        if (this.searchPhrase) {
          await this.fetchByKeyword(this.searchPhrase, ++this.page)
        } else if (this.selectedCollection) {
          await this.fetchByCollection(this.selectedCollection, ++this.page)
        } else {
          await this.fetchTrending(++this.page)
        }
      }
    }
  }
})
